import { Button } from '@/components/ui/button'
import { DataTableColumnHeader } from '@/components/ui/data-table'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TableSkeletonRows,
} from '@/components/ui/table'
import { useServiceRegistryQuery } from '@/hooks/api/use-service-registry-query'
import { cn } from '@/lib/utils'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  getExpandedRowModel,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { usePersistentPagination } from '@/hooks/use-persistent-pagination'
import { usePersistentSorting } from '@/hooks/use-persistent-sorting'
import { T, useTranslate } from '@tolgee/react'
import React from 'react'
import {
  ServiceRegistryOptionsMenu,
  AddServiceRegistryDialog,
  ServiceRegistryToolsDialog,
} from './_components'
import { PlusIcon } from '@/components/icons/plus-icon'
import { DatabaseIcon } from '@/components/icons/database-icon'
import { useServiceOAuthQueryState } from './_hooks/use-service-oauth-query-state'
import { DEFAULT_PAGINATION_OPTIONS, PaginationControls } from '@/components/ui/pagination-controls'
import { PageTemplate } from '@/app/_common/page-template'
import { TooltipIconButton } from '@/components/ui/tooltip-icon-button'
import { ChevronDownIcon, ChevronRightIcon } from 'lucide-react'
import { WarningIcon } from '@/components/icons/warning-icon'

export type ServiceRegistryTableDef = {
  name: string
  url: string
  serviceId: string
  serviceType: string
  options?: string
  errorDescription?: string
}

export type ServiceToolsDialogData = {
  serviceName: string
  tools: string[]
}

export function ServiceRegistryTab() {
  const { t } = useTranslate()
  const [isAddDialogOpen, setIsAddDialogOpen] = React.useState(false)
  const [isToolsDialogOpen, setIsToolsDialogOpen] = React.useState(false)
  const [toolsDialogData, setToolsDialogData] = React.useState<ServiceToolsDialogData>({
    serviceName: '',
    tools: [],
  })
  const { oauthParams, isOAuthSuccess, isOAuthError } = useServiceOAuthQueryState()

  const [sorting, setSorting] = usePersistentSorting({
    key: 'serviceRegistry-table-sorting',
    initialSorting: [{ id: 'updated_at', desc: true }],
  })
  const [pagination, setPagination] = usePersistentPagination({
    key: 'serviceRegistry-table-pagination',
    initialPagination: {
      pageIndex: 0,
      pageSize: DEFAULT_PAGINATION_OPTIONS.defaultPageSize,
    },
  })
  const serviceRegistryQuery = useServiceRegistryQuery({
    searchParams: {
      page: pagination.pageIndex + 1,
      size: pagination.pageSize,
      sort_by: sorting?.[0]?.id || 'updated_at',
      sort_order: sorting?.[0]?.id ? (sorting?.[0]?.desc ? 'desc' : 'asc') : 'desc',
    },
  })
  const columnDefs = React.useMemo<ColumnDef<ServiceRegistryTableDef>[]>(
    () => [
      {
        id: 'errorDescription',
        size: 20,
        cell: ({ row }) => {
          if (row.getCanExpand()) {
            return (
              <div className='flex items-center gap-1'>
                <div className='rounded-full bg-error-50 text-error-300'>
                  <WarningIcon />
                </div>
                <TooltipIconButton
                  keyName={'schedule.table.workflows.expand'}
                  onClick={() => row.toggleExpanded()}
                  size={'auto'}
                  className='text-neutral-150'
                >
                  {row.getIsExpanded() ? <ChevronDownIcon /> : <ChevronRightIcon />}
                </TooltipIconButton>
              </div>
            )
          }
          return null
        },
      },
      {
        accessorKey: 'name',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='serviceRegistry.table.headers.name'>
            <T keyName='serviceRegistry.table.headers.name' />
          </DataTableColumnHeader>
        ),
        cell: ({ cell }) => {
          const value = cell.getValue<string>()
          return <p className='min-w-80 font-bold'>{value}</p>
        },
      },
      {
        accessorKey: 'url',
        header: ({ column }) => (
          <DataTableColumnHeader column={column} titleKey='serviceRegistry.table.headers.url'>
            <T keyName='serviceRegistry.table.headers.url' />
          </DataTableColumnHeader>
        ),
        cell: ({ cell }) => {
          const value = cell.getValue<string>()
          return <p className='font-normal'>{value}</p>
        },
      },
      {
        accessorKey: 'serviceType',
        header: () => (
          <p className='ml-1'>
            <T keyName='serviceRegistry.table.headers.type' />
          </p>
        ),
        cell: ({ cell }) => {
          const value = cell.getValue<string>()
          return (
            <div className='flex items-center gap-1'>
              {value.startsWith('MCP') ? (
                <>
                  <DatabaseIcon className='size-6 flex-shrink-0' />
                  <p className='font-normal'>MCP</p>
                </>
              ) : (
                <p className='font-normal'> - </p>
              )}
            </div>
          )
        },
      },
      {
        accessorKey: 'options',
        header: () => (
          <p className='pr-4 text-center'>
            <T keyName='serviceRegistry.table.headers.actions' />
          </p>
        ),
        size: 20,
        cell: ({ row }) => {
          return (
            <div className='flex w-full items-center justify-center pr-4'>
              <ServiceRegistryOptionsMenu
                setIsToolsDialogOpen={setIsToolsDialogOpen}
                setToolsDialogData={setToolsDialogData}
                serviceData={row.original}
              />
            </div>
          )
        },
      },
    ],
    [],
  )

  const tableData = React.useMemo(
    () =>
      serviceRegistryQuery.data?.items?.map((w) => ({
        name: w.name,
        url: w.url,
        serviceId: w.id,
        serviceType: w.service_type,
        errorDescription: w?.error_description,
      })) ?? [],
    [serviceRegistryQuery.data?.items],
  )

  const table = useReactTable<ServiceRegistryTableDef>({
    data: tableData,
    columns: columnDefs,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowCanExpand: (row) => Boolean(row.original.errorDescription),
    getExpandedRowModel: getExpandedRowModel(),
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    manualPagination: true,
    manualSorting: true,
    enableFilters: false,
    sortDescFirst: true,
    rowCount: serviceRegistryQuery.data?.total,
    pageCount: serviceRegistryQuery.data?.pages,
    state: { pagination, sorting },
  })

  const { rows } = table.getRowModel()
  const isTableLoading =
    serviceRegistryQuery.status === 'pending' ||
    (serviceRegistryQuery.isFetching && rows.length === 0)

  const handleAddButtonClick = () => {
    setIsAddDialogOpen(true)
  }

  React.useEffect(() => {
    if ((isOAuthSuccess || isOAuthError) && !oauthParams?.serviceId && !isAddDialogOpen) {
      setIsAddDialogOpen(true)
    }
  }, [isOAuthSuccess, isOAuthError])

  return (
    <div className='flex h-full w-full grow flex-col gap-6'>
      <PageTemplate.Header className='justify-between'>
        <div>
          <PageTemplate.H4 className='font-normal text-neutral-500'>
            <T keyName='serviceRegistry.subtitle' />
          </PageTemplate.H4>
        </div>
        <div>
          <Button
            onClick={handleAddButtonClick}
            className='flex items-center gap-2'
            title={t('serviceRegistry.addButton.title')}
          >
            <T keyName='serviceRegistry.addButton.title' />
            <PlusIcon className='size-3 text-mango-200' />
          </Button>
          {isAddDialogOpen && (
            <AddServiceRegistryDialog
              open={isAddDialogOpen}
              onOpenChange={setIsAddDialogOpen}
              setIsToolsDialogOpen={setIsToolsDialogOpen}
              setToolsDialogData={setToolsDialogData}
            />
          )}
          {isToolsDialogOpen && (
            <ServiceRegistryToolsDialog
              open={isToolsDialogOpen}
              onOpenChange={setIsToolsDialogOpen}
              toolsDialogData={toolsDialogData}
              setToolsDialogData={setToolsDialogData}
            />
          )}
        </div>
      </PageTemplate.Header>
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <TableHead
                  key={header.id}
                  colSpan={header.colSpan}
                  style={{ width: `${header.getSize()}px` }}
                  data-state={
                    (header.column.getIsSorted() || header.column.getIsFiltered()) && 'selected'
                  }
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                </TableHead>
              ))}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {isTableLoading && <TableSkeletonRows cellCount={columnDefs.length} />}
          {serviceRegistryQuery.status === 'error' && (
            <TableRow>
              <TableCell colSpan={columnDefs.length} className='pb-6 text-center text-error-300'>
                <T keyName='shared.error.system' />
              </TableCell>
            </TableRow>
          )}
          {serviceRegistryQuery.status === 'success' &&
            rows.length > 0 &&
            rows.map((row) => (
              <React.Fragment key={row.id}>
                <TableRow
                  data-state={(row.getIsSelected() || row.getIsExpanded()) && 'selected'}
                  className='h-[72px] bg-neutral-white'
                >
                  {row.getVisibleCells().map((cell) => (
                    <TableCell
                      key={cell.id}
                      className={cn(`w-[${cell.column.getSize()}px]`)}
                      data-state={
                        (cell.column.getIsSorted() || cell.column.getIsFiltered()) && 'selected'
                      }
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
                {row.getIsExpanded() && (
                  <TableRow data-state={row.getIsExpanded() && 'selected'}>
                    <TableCell colSpan={row.getVisibleCells().length} className='pr-6'>
                      <p className='text-error-300'>{row.original.errorDescription}</p>
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            ))}
          {serviceRegistryQuery.status === 'success' &&
            rows?.length === 0 &&
            !serviceRegistryQuery.isFetching && (
              <TableRow>
                <TableCell colSpan={columnDefs.length} className='h-24 text-center'>
                  <T keyName='serviceRegistry.table.noResults' />
                </TableCell>
              </TableRow>
            )}
        </TableBody>
      </Table>
      {serviceRegistryQuery.status === 'success' && (
        <div className='mb-6'>
          <PaginationControls
            table={table}
            isTableLoading={isTableLoading}
            selectedPageIndex={pagination.pageIndex}
          />
        </div>
      )}
    </div>
  )
}
